import {Text} from '@dropbox/dig-components/typography';
import {Split, Stack} from '@dropbox/dig-foundations';
import {useQuery} from '@tanstack/react-query';
import {openAiSearchAndAskAtom} from 'atoms/aiSearch';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {Employee, ProfilePage} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import type {ChatMention} from 'components/DSYS/chat/chatMentions';
import {Title} from 'components/DSYS/Title';
import {
  type ProfileAskKind,
  profileAskQuestions,
  profileRelation,
} from 'components/profile/profileAsk';
import {sortEmployeeTeams} from 'components/profile/sortEmployeeTeams';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {useNavigate} from 'react-router';

import {PeopleRows, PeopleRowsSkeleton} from './PeopleRows';
import {profileQuery} from './profiles';
import {SearchRow} from './searchRows';
import {TeamCards} from './TeamCards';
import {AskRow, Row, RowList} from './Typography';
import {UpNext} from './UpNext';

/** An Ask question about anything in the panel, as the same row as everywhere else. */
export const AskLine = ({
  question,
  label = question,
  mentions,
  profileAsk,
}: {
  question: string;
  label?: string;
  mentions?: ChatMention[];
  /** A question about a person: what the server prefetches for it, as on their profile. */
  profileAsk?: ProfileAskKind;
}) => {
  const openAiSearchAndAsk = useSetAtom(openAiSearchAndAskAtom);
  return (
    <AskRow
      label={label}
      onClick={() => openAiSearchAndAsk(question, 'search_preview', {mentions, profileAsk})}
    />
  );
};

/**
 * The questions about a person, the same ones as on their profile. Closer in
 * (your manager, reports, peers) they're known at once; for anyone else they
 * name the person's team and depend on their chain, so they wait for the
 * profile rather than change under the pointer.
 */
const usePersonAsks = (person: Employee, profile?: ProfilePage) => {
  const viewer = useAtomValue(loggedInEmployeeAtom);
  if (!viewer.employee.ldap) return [];
  const relation = profileRelation({
    person,
    personReportingLine: profile?.reportingLine ?? [],
    viewer,
  });
  if (!profile && (relation === 'org' || relation === 'far')) return [];
  const team = profile ? sortEmployeeTeams(profile.employee, profile.employeeTeams)[0] : undefined;
  return profileAskQuestions(person.name, relation, team?.name);
};

/**
 * A person in the panel: who they are (the view's one bold line), Ask about
 * them (questions shaped by how they relate to you), then where they sit.
 * Clicking the name or avatar opens the profile; faces walk the org here.
 */
export const PersonPreview = ({
  person,
  onOpen,
  onSelect,
  hideHeader = false,
  onSelectRow,
}: {
  person: Employee;
  /** Go to their profile. */
  onOpen: (person: Employee) => void;
  /** Show someone in the panel instead, so faces walk the org without leaving the box. */
  onSelect?: (person: Employee) => void;
  /** Show anything else (a team) in the panel; without it, teams open their page. */
  onSelectRow?: (row: SearchRow) => void;
  /** Inside an open list row that already shows their name and role. */
  hideHeader?: boolean;
}) => {
  const pick = onSelect ?? onOpen;
  const navigate = useNavigate();
  const {employee: me} = useAtomValue(loggedInEmployeeAtom);
  const isMe = person.ldap === me.ldap;
  const {data: profile, isLoading} = useQuery(profileQuery(person.ldap));
  const chain = (profile?.reportingLine ?? []).slice(1);
  const managerId = profile?.employee.manager_id;
  // The profile page's lists, in the server's order, everyone (the pane
  // scrolls). Peers leave out their manager and their own reports, and
  // neither list has the placeholder account or the person themself.
  const listed = (member: Employee) => member.ldap !== 'donotuse' && member.ldap !== person.ldap;
  const reports = (profile?.directReports ?? []).filter(listed);
  const peers = (profile?.teamMembers ?? []).filter(
    (member) =>
      listed(member) &&
      member.user_id !== managerId &&
      !reports.some(({ldap}) => ldap === member.ldap)
  );
  const asks = usePersonAsks(person, profile);
  const teams = profile ? sortEmployeeTeams(profile.employee, profile.employeeTeams) : [];
  const mention: ChatMention = {
    // Recents only carry the ldap, so take the id from the loaded profile.
    employee_id: profile?.employee.user_id ?? person.user_id,
    name: person.name,
    ldap: person.ldap,
  };

  return (
    <Stack gap="44">
      <Stack gap="12">
        {/* The whole header is one target: it opens the profile. */}
        {!hideHeader && (
          <RowList>
            <Row onClick={() => onOpen(person)}>
              <Split gap="16" alignY="center">
                <Split.Item>
                  <Avatar user={person} size="xlarge" />
                </Split.Item>
                <Split.Item width="fill" style={{minWidth: 0}}>
                  <Title size={18}>{person.name}</Title>
                  <Text color="subtle" tagName="div">
                    {person.role}
                  </Text>
                </Split.Item>
              </Split>
            </Row>
          </RowList>
        )}
      </Stack>

      {/* What to ask comes first, unlabeled, like the prep line on your own card. */}
      {asks.length > 0 && (
        <Stack gap="8">
          <RowList>
            {asks.map(({kind, label, question}) => (
              <AskLine
                key={kind}
                question={question}
                label={label}
                mentions={[mention]}
                profileAsk={kind}
              />
            ))}
          </RowList>
        </Stack>
      )}

      {isMe && <UpNext />}

      <TeamCards
        teams={teams}
        onSelect={(row) =>
          onSelectRow
            ? onSelectRow(row)
            : row.kind === 'team' && navigate(`/teams/${row.team.slug}`)
        }
      />

      {isLoading ? (
        <PeopleRowsSkeleton />
      ) : (
        <>
          <PeopleRows label={t('search_box_reports_to')} people={chain} onPick={pick} chain />
          <PeopleRows label={t('peers')} people={peers} onPick={pick} grid />
          <PeopleRows label={t('reports')} people={reports} onPick={pick} grid />
        </>
      )}
    </Stack>
  );
};
